import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Login as LoginIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/authStore';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, resendVerification } = useAuthStore();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [loading, setLoading] = useState(false);

  // Mensajes de error que llegan por la URL (p. ej. login con Google de un correo no aprobado)
  useEffect(() => {
    const e = searchParams.get('error');
    if (!e) return;
    const MAP = {
      not_approved: 'Tu correo aún no tiene acceso aprobado a la beta. Solicítalo en la pantalla de inicio.',
      alta: 'No se pudo crear tu cuenta. Intenta de nuevo.',
      token_google: 'No se pudo validar con Google. Intenta de nuevo.',
      google: 'Error al iniciar con Google. Intenta de nuevo.',
      userinfo: 'No se pudo leer tu perfil de Google. Intenta de nuevo.',
      email_no_verificado: 'Tu correo de Google no está verificado.',
      estado_invalido: 'Sesión de Google inválida. Vuelve a intentar.',
    };
    setError(MAP[e] || 'No se pudo iniciar sesión.');
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setResendMsg('');
    setLoading(true);

    const result = await login(formData.username, formData.password);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error || 'Error al iniciar sesión');
      setNeedsVerification(!!result.needsVerification);
    }

    setLoading(false);
  };

  const handleResend = async () => {
    setResendMsg('');
    const res = await resendVerification(formData.username);
    setResendMsg(res.message || (res.success ? 'Enlace reenviado.' : (res.error || 'No se pudo reenviar.')));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 4,
              borderRadius: 4,
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                Bienvenido de nuevo
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Ingresa tus credenciales para continuar
              </Typography>
            </Box>

            {error && (
              <Alert
                severity={needsVerification ? 'warning' : 'error'}
                sx={{ mb: 3 }}
                action={
                  needsVerification ? (
                    <Button color="inherit" size="small" onClick={handleResend}>
                      Reenviar
                    </Button>
                  ) : null
                }
              >
                {error}
              </Alert>
            )}

            {resendMsg && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {resendMsg}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Usuario"
                name="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                margin="normal"
                required
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Contraseña"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ textAlign: 'right', mt: 1, mb: 3 }}>
                <Link to="/recuperar-contrasena" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="primary">
                    ¿Olvidaste tu contraseña?
                  </Typography>
                </Link>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                disabled={loading}
                startIcon={<LoginIcon />}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                }}
              >
                {loading ? 'Ingresando...' : 'Iniciar Sesión'}
              </Button>
            </form>

            <Divider sx={{ my: 3 }}>
              <Typography variant="caption" color="text.secondary">o</Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              size="large"
              component="a"
              href="/api/auth/google/login"
              startIcon={<GoogleIcon />}
              sx={{ py: 1.3, mb: 1, fontWeight: 600 }}
            >
              Continuar con Google
            </Button>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                ¿No tienes cuenta?{' '}
                <Link to="/register" style={{ textDecoration: 'none' }}>
                  <Typography component="span" variant="body2" color="primary" fontWeight={600}>
                    Regístrate aquí
                  </Typography>
                </Link>
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Typography component="span" variant="body2" color="text.secondary">
                  ← Volver al inicio
                </Typography>
              </Link>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Login;